/**
 * Frontend service for the manual purchase flow.
 *
 * Security boundary:
 * - Authorization, RBAC, rate limiting, file validation, and price resolution
 *   are enforced server-side.
 * - This service only handles transport, request shaping, body stripping,
 *   lightweight client-side validation, and frontend-friendly error handling.
 * - S3 uploads use direct XHR to the presigned URL and must not include
 *   application auth cookies.
 */

import { authenticatedFetch } from '../lib/apiClient';
import { buildApiUrl } from './apiUrl.js';
import { posthog } from '../lib/posthog';
import type {
  UploadIntentResponse,
  UploadIntentData,
  CompleteUploadResponse,
  CompleteUploadData,
  JobStatusResponse,
  JobStatusData,
  QuoteResponse,
  QuoteData,
  CheckoutResponse,
  CheckoutData,
  BillingInterval,
} from '../types/manuals';

const MAX_MANUAL_UPLOAD_BYTES = 100_000_000;
const ALLOWED_MANUAL_MIME_TYPES = new Set(['application/pdf']);

class ManualPurchaseService {
  async getUploadIntent(params: {
    filename: string;
    fileSizeBytes: number;
    mimeType: string;
  }): Promise<UploadIntentData> {
    if (params.fileSizeBytes > MAX_MANUAL_UPLOAD_BYTES) {
      posthog.capture('manual_upload_rejected', { reason: 'file_too_large', file_size_bytes: params.fileSizeBytes });
      throw new Error('File too large');
    }
    if (!ALLOWED_MANUAL_MIME_TYPES.has(params.mimeType)) {
      posthog.capture('manual_upload_rejected', { reason: 'unsupported_mime_type', mime_type: params.mimeType });
      throw new Error('Unsupported file type');
    }

    posthog.capture('manual_upload_started', {
      filename: params.filename,
      file_size_bytes: params.fileSizeBytes,
    });

    const res = await authenticatedFetch(buildApiUrl('/api/admin/manuals/upload-intent'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    const json = (await res.json()) as UploadIntentResponse;
    if (!json.success) {
      posthog.capture('manual_upload_intent_failed', { filename: params.filename });
      throw new Error((json as any).error?.message ?? 'Upload intent failed');
    }
    console.log('[ManualUpload] Upload intent response', {
      uploadId: json.data.uploadId,
      s3Key: json.data.s3Key,
      urlPrefix: json.data.s3UploadUrl?.substring(0, 80),
    });
    return json.data;
  }

  async uploadToS3(
    presignedUrl: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<void> {
    console.log('[ManualUpload] Starting upload to signed URL', {
      url: presignedUrl.substring(0, 100) + '...',
      fileSize: file.size,
      fileType: file.type,
      fileName: file.name,
    });

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          console.log(`[ManualUpload] Progress: ${pct}%`);
          onProgress?.(pct);
        }
      };
      xhr.onload = () => {
        console.log('[ManualUpload] XHR completed', {
          status: xhr.status,
          statusText: xhr.statusText,
          responseHeaders: xhr.getAllResponseHeaders(),
          responseBody: xhr.responseText?.substring(0, 500),
        });
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Storage upload failed: ${xhr.status} ${xhr.statusText} - ${xhr.responseText?.substring(0, 200)}`));
      };
      xhr.onerror = () => {
        console.error('[ManualUpload] XHR network error', {
          status: xhr.status,
          readyState: xhr.readyState,
        });
        reject(new Error('Storage upload network error'));
      };
      xhr.send(file);
    });
  }

  async completeUpload(params: { uploadId: string }): Promise<CompleteUploadData> {
    const res = await authenticatedFetch(buildApiUrl('/api/admin/manuals/complete-upload'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadId: params.uploadId }),
    });
    const json = (await res.json()) as CompleteUploadResponse;
    if (!json.success) {
      posthog.capture('manual_upload_complete_failed', { upload_id: params.uploadId });
      throw new Error((json as any).error?.message ?? 'Complete upload failed');
    }
    posthog.capture('manual_upload_completed', { upload_id: params.uploadId });
    return json.data;
  }

  async getJobStatus(uploadId: string): Promise<JobStatusData> {
    const res = await authenticatedFetch(buildApiUrl(`/api/admin/manuals/${uploadId}/job-status`));
    const json = (await res.json()) as JobStatusResponse;
    if (!json.success) throw new Error((json as any).error?.message ?? 'Status check failed');
    return json.data;
  }

  async getQuote(uploadId: string): Promise<QuoteData | null> {
    const res = await authenticatedFetch(buildApiUrl(`/api/admin/manuals/${uploadId}/quote`));
    if (res.status === 404) {
      const json = await res.json().catch(() => null);
      if (json?.error?.code === 'UPLOAD_NOT_FOUND') {
        throw new Error(json.error.message ?? 'Upload not found');
      }
      return null;
    }
    const json = (await res.json()) as QuoteResponse;
    if (!json.success) throw new Error((json as any).error?.message ?? 'Quote fetch failed');
    posthog.capture('quote_received', {
      upload_id: uploadId,
      pricing_tier: json.data.pricingTier,
      monthly_price_cents: json.data.monthlyPriceCents,
      tier_label: json.data.tierLabel,
      review_required: json.data.reviewRequired,
    });
    return json.data;
  }

  async createCheckout(params: {
    uploadId: string;
    quoteId: string;
    billingInterval: BillingInterval;
  }): Promise<CheckoutData> {
    posthog.capture('checkout_started', {
      upload_id: params.uploadId,
      quote_id: params.quoteId,
      billing_interval: params.billingInterval,
    });
    const res = await authenticatedFetch(buildApiUrl(`/api/admin/manuals/${params.uploadId}/checkout`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quoteId: params.quoteId, billingInterval: params.billingInterval }),
    });
    const json = (await res.json()) as CheckoutResponse;
    if (!json.success) {
      posthog.capture('checkout_failed', { upload_id: params.uploadId, quote_id: params.quoteId });
      throw new Error((json as any).error?.message ?? 'Checkout failed');
    }
    return json.data;
  }
}

export const manualPurchaseService = new ManualPurchaseService();
